/** Forgot / Reset password — the two-step self-service flow. */
import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();

  const [step, setStep] = useState(token ? "reset" : "request");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (token) setStep("reset"); }, [token]);

  const request = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault(); setErr("");
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      toast.success("Password updated — please sign in.");
      nav("/login", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Reset failed.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#1B1D1F] font-sans antialiased">
      <header className="border-b border-[#E7E7E4]">
        <div className="max-w-5xl mx-auto flex items-center h-14 px-6">
          <div className="text-[13px] font-semibold tracking-[0.08em] text-[#0A1A2F]">SUNTRONIX ONE</div>
          <span className="mx-3 text-[#D1D1CD] text-[11px]">•</span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#5A5C60]">
            Password recovery
          </span>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 pt-24 pb-12">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-[12px] text-[#5A5C60] hover:text-[#0A1A2F] mb-6"
          data-testid="forgot-back">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back to sign in
        </Link>

        {step === "request" && !done && (
          <>
            <h1 className="text-[24px] font-medium text-[#0A1A2F] tracking-[-0.01em]">
              Reset your password
            </h1>
            <p className="text-[13px] text-[#5A5C60] mt-2 leading-relaxed">
              Enter the work email associated with your ONE account. If we find a match,
              we&apos;ll send a secure link to reset your password.
            </p>
            <form onSubmit={request} className="mt-8 space-y-4" data-testid="forgot-form">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#5A5C60]">Work email</span>
                <input
                  type="email" required autoFocus value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="mt-2 w-full h-11 px-3 rounded-md border border-[#E7E7E4] bg-white text-[15px] text-[#0A1A2F] placeholder-[#B4B4B0] focus:outline-none focus:border-[#0A1A2F] focus:ring-2 focus:ring-[#0A1A2F]/10"
                  data-testid="forgot-email"
                />
              </label>
              {err && <div role="alert" className="text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
              <button
                type="submit" disabled={loading || !email}
                className="w-full h-11 rounded-md bg-[#0A1A2F] text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="forgot-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </button>
            </form>
          </>
        )}

        {step === "request" && done && (
          <div data-testid="forgot-sent">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" strokeWidth={1.5} />
            <h1 className="mt-4 text-[24px] font-medium text-[#0A1A2F]">Check your inbox</h1>
            <p className="text-[13px] text-[#5A5C60] mt-2 leading-relaxed">
              If an account exists for <b>{email}</b>, we&apos;ve sent a link to reset your password.
              The link expires in 30 minutes and can only be used once.
            </p>
            <Link to="/login" className="mt-8 inline-block h-11 px-4 rounded-md bg-[#0A1A2F] text-white font-medium leading-[44px]">
              Back to sign in
            </Link>
          </div>
        )}

        {step === "reset" && (
          <>
            <h1 className="text-[24px] font-medium text-[#0A1A2F] tracking-[-0.01em]">
              Choose a new password
            </h1>
            <p className="text-[13px] text-[#5A5C60] mt-2 leading-relaxed">
              Passwords are at least 8 characters. Choose one you have not used before.
            </p>
            <form onSubmit={confirmReset} className="mt-8 space-y-4" data-testid="reset-form">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#5A5C60]">New password</span>
                <input
                  type="password" required autoFocus minLength={8} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 w-full h-11 px-3 rounded-md border border-[#E7E7E4] bg-white text-[15px] text-[#0A1A2F] focus:outline-none focus:border-[#0A1A2F] focus:ring-2 focus:ring-[#0A1A2F]/10"
                  data-testid="reset-new-password"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#5A5C60]">Confirm new password</span>
                <input
                  type="password" required minLength={8} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 w-full h-11 px-3 rounded-md border border-[#E7E7E4] bg-white text-[15px] text-[#0A1A2F] focus:outline-none focus:border-[#0A1A2F] focus:ring-2 focus:ring-[#0A1A2F]/10"
                  data-testid="reset-confirm-password"
                />
              </label>
              {err && <div role="alert" className="text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
              <button
                type="submit" disabled={loading}
                className="w-full h-11 rounded-md bg-[#0A1A2F] text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="reset-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
